import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  LinearProgress,
  Alert,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { SelectChangeEvent } from "@mui/material/Select";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import secureLocalStorage from "react-secure-storage";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDefaultStyles } from "../../common/styles/default";
import colors from "../../common/styles/colors";
import Banner from "../../common/components/banner";
import banner from "../../assets/images/banners/banner_title_page_list_users.png";
import CsvTable from "./csv/CsvTable";
import CsvActions from "./csv/CsvActions";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TitleForm from "../../common/components/titleform";
import Papa from "papaparse";
import { useStyles } from "../styles/index.styles";

import UsersService from "../../services/UsersService";
import EntitiesService from "../../services/EntitiesService";
import StudentsGroupService from "../../services/StudentsGroupService";

import Avatar from "@mui/material/Avatar";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AvatarHiplade from "../../common/components/avatarhiplade";

// Adicione uma constante configurável para o tamanho do lote
const BATCH_SIZE = 20;

export default function CsvPreview() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const styles = useStyles();
  const [csvData, setCsvData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [fileLoaded, setFileLoaded] = useState<boolean>(false);
  const [entities, setEntities] = useState<any[]>([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [passwordOverride, setPasswordOverride] = useState("");
  const [avatarPreviews, setAvatarPreviews] = useState<
    { url: string; isAccessible: boolean }[]
  >([]);

  // Estados para seleção/criação de turmas
  const [studentsGroups, setStudentsGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [isLoadingGroups, setIsLoadingGroups] = useState<boolean>(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState<boolean>(false);
  const [groupInputValue, setGroupInputValue] = useState<string>("");

  // Estado para o modal de confirmação
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

  // Estados para controlar o progresso da importação
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<{
    processed: number;
    total: number;
  }>({ processed: 0, total: 0 });

  // Adicionar novo estado para armazenar os resultados da API
  const [resultados, setResultados] = useState<
    { name: string; email: string; success: boolean }[]
  >([]);
  const [cadastroConcluido, setCadastroConcluido] = useState<boolean>(false);

  const csvSummary = useMemo(() => {
    const total = csvData.length;
    let linhasComErro = 0;
    let obrigatoriosPendentes = 0;

    csvData.forEach((row) => {
      const faltaObrigatorio = row._entidade_vazio || row._password_vazio;
      if (faltaObrigatorio) {
        obrigatoriosPendentes += 1;
      }

      const possuiErroBasico = row._status !== "valido";
      if (possuiErroBasico || faltaObrigatorio) {
        linhasComErro += 1;
      }
    });

    const linhasValidadas = Math.max(total - linhasComErro, 0);

    return {
      total,
      linhasValidadas,
      linhasComErro,
      obrigatoriosPendentes,
    };
  }, [csvData]);

  const hasMissingEntity = useMemo(
    () => csvData.some((row) => row._entidade_vazio),
    [csvData]
  );

  const hasMissingPassword = useMemo(
    () => csvData.some((row) => row._password_vazio),
    [csvData]
  );

  const normalizeCsvHeader = (rawHeader: string) => {
    if (!rawHeader) return rawHeader;
    const sanitized = rawHeader
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

    switch (sanitized) {
      case "nome":
        return "Nome";
      case "email":
        return "E-mail";
      case "cpf":
        return "CPF";
      case "rg":
        return "RG";
      case "matricula":
      case "ra":
        return "Matricula";
      case "avatar":
        return "Avatar";
      case "role_id":
      case "roleid":
        return "role_id";
      case "entidade":
      case "entidadenome":
        return "Entidade";
      case "entity_id":
      case "entidadeid":
      case "entidade_id":
        return "entity_id";
      case "studentsgroup_id":
      case "turmaid":
      case "turma_id":
        return "studentsgroup_id";
      case "turma":
        return "Turma";
      case "password":
      case "senha":
        return "Password";
      default:
        return rawHeader;
    }
  };

  const buildVisibleHeaders = (data: any[]) => {
    const optionalColumns = ["CPF", "Matricula", "Avatar", "Turma"];
    const optionalHeaders = optionalColumns.filter((field) =>
      data.some((row) => String(row[field] || "").trim().length > 0)
    );

    return [
      "Nome",
      "E-mail",
      "Entidade",
      "Password",
      ...optionalHeaders,
      "Perfil",
      "Status",
    ];
  };

  const persistCsvData = (data: any[]) => {
    setCsvData(data);
    secureLocalStorage.setItem("csvData", JSON.stringify(data));
  };

  const generateRandomPassword = (length = 12) => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
    let result = "";
    for (let i = 0; i < length; i += 1) {
      const randomIndex = Math.floor(Math.random() * chars.length);
      result += chars[randomIndex];
    }
    return result;
  };

  const fetchStudentsGroups = useCallback(async (entityId?: string) => {
    setIsLoadingGroups(true);
    try {
      const activeEntityId = entityId || selectedEntityId || secureLocalStorage.getItem("entity")?.toString() || "";
      const query = activeEntityId ? `entity_id=${activeEntityId}` : "";
      const response: any = await StudentsGroupService.getAll(1, 9999, query);
      const payload = response?.data;
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];
      setStudentsGroups(list);
    } catch (error) {
      console.error("Erro ao carregar turmas:", error);
    } finally {
      setIsLoadingGroups(false);
    }
  }, [selectedEntityId]);

  const applyEntityToCsv = (entityId: string, entityName: string) => {
    if (!entityId || !entityName) {
      toast.warning("Selecione uma entidade para aplicar ao CSV.");
      return;
    }

    const updatedData = csvData.map((row) => ({
      ...row,
      Entidade: entityName,
      entity_id: entityId,
      _entidade_vazio: false,
    }));

    persistCsvData(updatedData);
    toast.success("Entidade aplicada a todos os registros.");
    fetchStudentsGroups(entityId);
  };

  const applyPasswordToCsv = (passwordValue: string) => {
    const sanitizedPassword = passwordValue.trim();
    if (!sanitizedPassword) {
      toast.warning("Informe uma senha para aplicar aos registros.");
      return;
    }

    const updatedData = csvData.map((row) => ({
      ...row,
      Password: sanitizedPassword,
      _password_vazio: false,
    }));

    persistCsvData(updatedData);
    toast.success("Senha aplicada a todos os registros.", {toastId: "password-apply-success"});
  };

  const applyGroupToCsv = (groupId: string | number, groupName: string) => {
    const updatedData = csvData.map((row) => ({
      ...row,
      Turma: groupName,
      studentsgroup_id: groupId ? String(groupId) : "",
    }));

    persistCsvData(updatedData);
    const visibleHeaders = buildVisibleHeaders(updatedData);
    setHeaders(visibleHeaders);
    secureLocalStorage.setItem("csvHeaders", JSON.stringify(visibleHeaders));

    if (groupId) {
      toast.success(`Turma "${groupName}" vinculada a todos os registros.`);
    } else {
      toast.info("Vínculo de turma removido dos registros.");
    }
  };

  const handleGroupChange = (newValue: any) => {
    setSelectedGroup(newValue);
    if (newValue && newValue.id) {
      applyGroupToCsv(newValue.id, newValue.name);
    } else {
      applyGroupToCsv("", "");
    }
  };

  const handleCreateGroup = async (nameToCreate: string) => {
    const trimmedName = nameToCreate.trim();
    if (!trimmedName) {
      toast.warning("Informe o nome da turma.");
      return;
    }

    const activeEntityId =
      selectedEntityId ||
      csvData.find((r) => r.entity_id)?.entity_id ||
      secureLocalStorage.getItem("entity")?.toString() ||
      "";

    if (!activeEntityId) {
      toast.error("Selecione uma entidade antes de criar a turma.");
      return;
    }

    setIsCreatingGroup(true);
    try {
      const response: any = await StudentsGroupService.store({
        name: trimmedName,
        entity_id: activeEntityId,
        id: "",
        parent_id: "",
      });

      const newGroup = response?.data || { id: response?.data?.id, name: trimmedName, entity_id: activeEntityId };
      toast.success(`Turma "${trimmedName}" criada com sucesso!`);
      
      setStudentsGroups((prev) => [...prev, newGroup]);
      setSelectedGroup(newGroup);
      applyGroupToCsv(newGroup.id, newGroup.name || trimmedName);
      setGroupInputValue("");
    } catch (error: any) {
      console.error("Erro ao criar turma:", error);
      const msg = error?.response?.data?.Error || error?.response?.data?.Msg || "Erro ao criar turma.";
      toast.error(msg);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const entityOptions = useMemo(() => {
    return entities.map((entity) => {
      const value = String(
        entity.id ?? entity.uuid ?? entity.value ?? entity.code ?? entity.name ?? ""
      );
      const label =
        entity.name ||
        entity.nome ||
        entity.razao_social ||
        entity.description ||
        entity.alias ||
        `Entidade ${value}`;

      return { value, label };
    });
  }, [entities]);

  const prepareRowsForApi = useCallback(
    (rows: any[]) => {
      const fallbackEntityId = secureLocalStorage.getItem("entity")?.toString() || "";
      let missingEntity = false;

      const sanitizedRows = rows.map((row) => {
        const {
          Perfil,
          _status,
          _nome_vazio,
          _email_vazio,
          _email_invalido,
          _email_formatado,
          ...rest
        } = row;

        const resolvedEntityId = row.entity_id || row.entityId || fallbackEntityId;
        if (!resolvedEntityId) {
          missingEntity = true;
        }

        return {
          ...rest,
          entity_id: resolvedEntityId,
          studentsgroup_id: row.studentsgroup_id || (selectedGroup?.id ? String(selectedGroup.id) : ""),
        };
      });

      if (missingEntity) {
        toast.error(
          "Há registros sem entidade definida. Selecione uma entidade para continuar."
        );
        return null;
      }

      return sanitizedRows;
    },
    [selectedGroup]
  );

  // Função para exibir toast usando Toastify
  const showToast = (
    message: string,
    severity: "success" | "error" | "info" | "warning" = "info"
  ) => {
    switch (severity) {
      case "success":
        toast.success(message);
        break;
      case "error":
        toast.error(message);
        break;
      case "warning":
        toast.warning(message);
        break;
      case "info":
      default:
        toast.info(message);
        break;
    }
  };

  // Referência para o input de arquivo oculto
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchEntities = useCallback(async () => {
    setIsLoadingEntities(true);
    try {
      const response = await EntitiesService.getAll(1, 9999);
      const payload: any = response?.data ?? [];
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];
      setEntities(list);
    } catch (error) {
      toast.error("Não foi possível carregar as entidades.");
    } finally {
      setIsLoadingEntities(false);
    }
  }, []);

  useEffect(() => {
    // Recuperar dados do CSV do localStorage
    const storedData = secureLocalStorage.getItem("csvData");
    const storedFileName = secureLocalStorage.getItem("csvFileName");
    const storedHeaders = secureLocalStorage.getItem("csvHeaders");

    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData as string);
        setCsvData(parsedData);
        setFileLoaded(true);

        // Usar os cabeçalhos armazenados ou definir padrão
        if (storedHeaders) {
          setHeaders(JSON.parse(storedHeaders as string));
        } else if (parsedData.length > 0) {
          setHeaders(buildVisibleHeaders(parsedData));
        }

        if (storedFileName) {
          setFileName(storedFileName as string);
        }
      } catch (error) {
        console.error("Erro ao analisar dados armazenados:", error);
        // Limpar dados inválidos
        secureLocalStorage.removeItem("csvData");
        secureLocalStorage.removeItem("csvFileName");
        secureLocalStorage.removeItem("csvHeaders");
      }
    }
  }, []);
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  const runAvatarPreview = useCallback(async (data: any[]) => {
    const avatarUrls = data
      .map((row) => row.Avatar)
      .filter(Boolean)
      .slice(0, 5); // Pega os 5 primeiros para amostra

    await sleep(2000); // Each waits 2s

    if (avatarUrls.length > 0) {
      const accessibilityChecks = await Promise.all(
        avatarUrls.map(async (url) => ({
          url,
          isAccessible: await checkImageAccessibility(url),
        }))
      );
      setAvatarPreviews(accessibilityChecks);

      if (accessibilityChecks.some((check) => !check.isAccessible)) {
        toast.warning(
          "Aviso: Alguns links de avatar podem não ser imagens públicas ou acessíveis. Verifique os links na pré-visualização."
        );
      }
    }
  }, []);

  useEffect(() => {
    if (csvData.length > 0) {
      runAvatarPreview(csvData);
    }
  }, [csvData, runAvatarPreview]);

  useEffect(() => {
    if (entities.length === 0) {
      fetchEntities();
    }
    fetchStudentsGroups();
  }, [fetchEntities, fetchStudentsGroups, entities.length]);

  const handleDownloadCsv = () => {
    if (csvData.length === 0) return;

    // Criar o conteúdo do CSV
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => headers.map((header) => row[header]).join(",")),
    ].join("\n");

    // Criar um blob e link para download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName || "exported_data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteFile = () => {
    // Limpar os dados do CSV
    setCsvData([]);
    setHeaders([]);
    setFileName("");
    setFileLoaded(false);
    setSelectedGroup(null);

    // Remover dados do localStorage
    secureLocalStorage.removeItem("csvData");
    secureLocalStorage.removeItem("csvFileName");
    secureLocalStorage.removeItem("csvHeaders");
  };

  const handleFileSelect = () => {
    // Aciona o clique no input de arquivo oculto
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleEntitySelection = (event: SelectChangeEvent<string>) => {
    const entityId = event.target.value;
    setSelectedEntityId(entityId);

    const option = entityOptions.find((item) => item.value === entityId);
    if (option) {
      applyEntityToCsv(option.value, option.label);
    }
  };

  const handlePasswordInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordOverride(event.target.value);
  };

  const handleApplyPasswordClick = () => {
    applyPasswordToCsv(passwordOverride);
  };

  const handleGeneratePassword = () => {
    const generated = generateRandomPassword();
    setPasswordOverride(generated);
    applyPasswordToCsv(generated);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Verificar se o arquivo é CSV
    if (!file.name.toLowerCase().endsWith(".csv")) {
      alert("Por favor, selecione um arquivo CSV válido.");
      return;
    }

    // Usar Papa Parse para processar o CSV
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      delimiter: ";",
      transformHeader: normalizeCsvHeader,
      complete: (results) => {
        let data = results.data as Record<string, string>[];

        if (data.length === 0) {
          alert("O arquivo CSV está vazio ou não contém dados válidos.");
          return;
        }

        // Função para validar e-mail usando regex
        const isValidEmail = (email: string): boolean => {
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          return emailRegex.test(email);
        };

        // Processar os dados
        const processedData = data.map((row) => {
          // Copiar todos os dados originais
          const processedRow: Record<string, any> = { ...row };

          const ensureField = (field: string) => {
            if (!(field in processedRow) || processedRow[field] === undefined) {
              processedRow[field] = "";
            }
          };

          [
            "Nome",
            "E-mail",
            "Entidade",
            "Password",
            "CPF",
            "RG",
            "Matricula",
            "Avatar",
            "entity_id",
            "role_id",
            "studentsgroup_id",
            "Turma",
          ].forEach(ensureField);

          // Adicionar campos de validação
          const normalizeValue = (field: string) => {
            const value = String(processedRow[field] || "").trim();
            processedRow[field] = value;
            return value;
          };

          const nome = normalizeValue("Nome");
          const email = normalizeValue("E-mail");
          const entidade = normalizeValue("Entidade");
          const password = normalizeValue("Password");
          normalizeValue("CPF");
          normalizeValue("RG");
          normalizeValue("Matricula");
          normalizeValue("Avatar");
          normalizeValue("entity_id");
          normalizeValue("role_id");
          normalizeValue("studentsgroup_id");
          normalizeValue("Turma");

          processedRow["_nome_vazio"] = !nome;
          processedRow["_email_vazio"] = !email;
          processedRow["_entidade_vazio"] = !entidade;
          processedRow["_password_vazio"] = !password;

          // Validar formato do e-mail se estiver preenchido
          const emailInvalido = email !== "" && !isValidEmail(email);
          processedRow["_email_invalido"] = emailInvalido;

          // Formatar o e-mail com o ícone se for inválido
          if (emailInvalido) {
            processedRow["_email_formatado"] = {
              valor: email,
              invalido: true,
              mensagem: "Formato de e-mail inválido",
            };
          } else {
            processedRow["_email_formatado"] = {
              valor: email,
              invalido: false,
            };
          }

          // Adicionar Perfil Discente
          processedRow["Perfil"] = "Discente";

          // Status geral - válido apenas se nome preenchido E email preenchido e válido
          const linhaValida = nome && email && isValidEmail(email);
          processedRow["_status"] = linhaValida ? "valido" : "invalido";

          return processedRow;
        });

        // Cabeçalhos para exibição
        const visibleHeaders = buildVisibleHeaders(processedData);

        secureLocalStorage.setItem("csvData", JSON.stringify(processedData));
        secureLocalStorage.setItem("csvFileName", file.name);
        secureLocalStorage.setItem(
          "csvHeaders",
          JSON.stringify(visibleHeaders)
        );

        setCsvData(processedData);
        setHeaders(visibleHeaders);
        setFileName(file.name);
        setFileLoaded(true);

        // Resetar o estado de cadastro concluído e resultados
        setCadastroConcluido(false);
        setResultados([]);
        setAvatarPreviews([]); // Limpar previews antigos
      },
      error: (error) => {
        console.error("Erro ao processar o arquivo CSV:", error);
        alert("Erro ao processar o arquivo. Verifique se é um CSV válido.");
      },
    });

    event.target.value = "";
  };

  const getGoogleDriveDirectLink = (url: string) => {
    const regex = /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/;
    const match = url.match(regex);
    if (match && match[1]) {
      return `https://lh3.googleusercontent.com/d/${match[1]}`;
    }
    return url;
  };

  const checkImageAccessibility = async (url: string) => {
    if (!url || (!url.startsWith("http://") && !url.startsWith("https://"))) {
      return false;
    }
    await sleep(2000); // Each waits 2s
    const directUrl = getGoogleDriveDirectLink(url);

    try {
      const response = await fetch(directUrl, { method: "HEAD", mode: "cors" });
      if (response.type === "opaque") {
        return true;
      }
      const contentType = response.headers.get("Content-Type");
      return contentType?.startsWith("image/") ?? false;
    } catch (error) {
      console.warn(`Não foi possível verificar a URL do avatar: ${url}`, error);
      return true;
    }
  };

  const handleSendToApi = async () => {
    if (csvData.length === 0) return;

    try {
      const validRows = prepareRowsForApi(
        csvData.filter((row) => row._status === "valido")
      );

      if (!validRows) {
        return;
      }

      if (validRows.length === 0) {
        showToast("Não há linhas válidas para enviar.", "warning");
        return;
      }

      // Definir o total para a barra de progresso
      setImportProgress({ processed: 0, total: validRows.length });

      // Mostrar modal de confirmação
      setConfirmDialogOpen(true);
    } catch (error) {
      console.error("Erro ao processar dados:", error);
      showToast(
        "Ocorreu um erro ao processar os dados. Por favor, tente novamente.",
        "error"
      );
    }
  };

  // Nova função para executar o envio após a confirmação
  const confirmSendToApi = async () => {
    setConfirmDialogOpen(false);
    setIsImporting(true);

    const validRows = prepareRowsForApi(
      csvData.filter((row) => row._status === "valido")
    );

    if (!validRows || validRows.length === 0) {
      setIsImporting(false);
      return;
    }

    // Definição exata das colunas exigidas pelo backend para o CSV de importação
    const targetCsvHeaders = [
      "name",
      "e-mail",
      "password",
      "entity_id",
      "ra",
      "cpf",
      "rg",
      "role_id",
      "studentsgroup_id",
    ];

    // Adicionar a coluna "Criado" se ainda não existir
    if (!headers.includes("Criado")) {
      setHeaders((prev) => [...prev, "Criado"]);
    }

    try {
      for (let i = 0; i < validRows.length; i += BATCH_SIZE) {
        const batch = validRows.slice(i, i + BATCH_SIZE);

        const csvContent = [
          targetCsvHeaders.join(","),
          ...batch.map((row) => {
            const mappedValues = [
              row.Nome || row.name || "",
              row["E-mail"] || row.email || row["e-mail"] || "",
              row.Password || row.password || "",
              row.entity_id || row.entityId || "",
              row.Matricula || row.ra || "",
              row.CPF || row.cpf || "",
              row.RG || row.rg || "",
              row.role_id || "5", // Role padrão de discente/aluno
              row.studentsgroup_id || "",
            ];
            return mappedValues
              .map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`)
              .join(",");
          }),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const csvFile = new File([blob], `usuarios_lote_${i / BATCH_SIZE + 1}.csv`, {
          type: "text/csv",
        });

        const formData = new FormData();
        formData.append("file", csvFile);

        try {
          const response = await UsersService.importUser(formData);

          if (response.status === 200 || response.status === 201) {
            if (response.data && response.data.results) {
              const apiResults = response.data.results;

              setCsvData((currentData) =>
                currentData.map((row) => {
                  const result = apiResults.find(
                    (item: { email: string }) => item.email === row["E-mail"]
                  );
                  if (result) {
                    return {
                      ...row,
                      Status: result.success
                        ? "Importado com sucesso"
                        : "Falha na importação",
                      Criado: result.success ? "Sim" : <span style={{ color: "#C0135A" }}>Não</span>,
                    };
                  }
                  return row;
                })
              );
            }
            showToast(response.data.Msg, "success");
          }
        } catch (batchError) {
          console.error(`Erro no lote ${i / BATCH_SIZE + 1}:`, batchError);
          showToast(`Ocorreu um erro no lote ${i / BATCH_SIZE + 1}.`, "error");
          // Atualizar as linhas do lote com erro
          setCsvData((currentData) =>
            currentData.map((row) => {
              const isInBatch = batch.some(
                (batchRow) => batchRow["E-mail"] === row["E-mail"]
              );
              if (isInBatch) {
                return {
                  ...row,
                  Status: "Falha na importação",
                  Criado: <span style={{ color: "#C0135A" }}>Não</span>,
                };
              }
              return row;
            })
          );
        }

        // Atualizar progresso
        setImportProgress((prev) => ({
          ...prev,
          processed: Math.min(prev.processed + BATCH_SIZE, validRows.length),
        }));
      }

      setCadastroConcluido(true);
      showToast("Importação finalizada!", "info");
    } catch (error: any) {
      console.error("Erro ao enviar dados para API:", error);
      const backendMsg =
        error?.response?.data?.Error ??
        error?.response?.data?.Msg ??
        error?.response?.data?.message;
      showToast(
        backendMsg ??
          "Ocorreu um erro ao importar os usuários. Por favor, tente novamente.",
        "error"
      );
    } finally {
      setIsImporting(false);
    }
  };

  // Função para cancelar o envio
  const cancelSendToApi = () => {
    setConfirmDialogOpen(false);
  };

  // Função para voltar para a página de lista de usuários
  const handleBack = () => {
    navigate("/users/list");
  };

  // Função para verificar se todos os status estão errados
  const verificarTodosStatusErrados = () => {
    return csvData.every(
      (row) => row._status === "invalido" || row._status === false
    );
  };

  // Função para verificar se o arquivo está vazio
  const arquivoEstaVazio = () => {
    return csvData.length === 0;
  };

  const isExactGroupMatch = useMemo(() => {
    const search = groupInputValue.trim().toLowerCase();
    if (!search) return true;
    return studentsGroups.some((g) => g.name?.trim().toLowerCase() === search);
  }, [studentsGroups, groupInputValue]);

  return (
    <Box>
      <Banner
        type="small"
        image={banner}
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
            <GroupsOutlinedIcon sx={{ width: "3em", height: "3em" }} />
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              Cadastro em lote
            </Typography>
          </Box>
        }
      />
      <Box>
        <Box
          sx={{
            width: "100%",
            position: "relative",
            paddingLeft: "140px",
            paddingRight: "220px",
            paddingTop: "60px",
            paddingBottom: "60px",

            [theme.breakpoints.down("sm")]: {
              position: "relative",
              ml: "25px",
              width: "94%",
              paddingLeft: "5px",
              paddingRight: "30px",
            },

            [theme.breakpoints.between("sm", "md")]: {
              position: "relative",
              ml: "25px",
              width: "94%",
              paddingLeft: "53px",
              paddingRight: "53px",
            },

            [theme.breakpoints.between("md", "lg")]: {
              position: "relative",
              ml: "25px",
              width: "92%",
              paddingLeft: "34px",
              paddingRight: "30px",
            },

            [theme.breakpoints.between("lg", "xl")]: {
              position: "relative",
              ml: "25px",
              width: "96%",
              paddingLeft: "65px",
              paddingRight: "75px",
            },
          }}
        >
          <TitleForm title="Arquivo selecionado" />
          <Typography>
            Veja o arquivo selecionado. Relembrando que o arquivo deve estar no
            formato .csv e deve conter os campos obrigatórios necessários para o
            cadastro, sendo eles: Nome, email, perfil desejado. Demais campos
            como número de matricula pode ser preenchido posteriormente pelo
            usuário.
          </Typography>

          {/* Input de arquivo oculto */}
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />

          <CsvActions
            onDownload={handleDownloadCsv}
            onDelete={handleDeleteFile}
            onAddFile={handleFileSelect}
            onSendToApi={handleSendToApi}
            fileName={fileName}
            fileLoaded={fileLoaded}
            isImporting={isImporting}
          />

          {fileLoaded && <TitleForm title="Usuários carregados" />}
          {fileLoaded && (
            <Typography>
              Abaixo é possível visualizar os usuários carregados pelo arquivo
              selecionado. A coluna Status indica se existe alguma
              irregularidade nas informações do usuário.{" "}
              <b>
                Caso existam irregularidades altere o arquivo original e
                recarregue-o.
              </b>
            </Typography>
          )}

          {fileLoaded && avatarPreviews.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <TitleForm title="Pré-visualização dos Avatares (Amostra)" />
              <Typography sx={{ mb: 2 }}>
                Abaixo estão os primeiros avatares encontrados no arquivo.
                Verifique se as imagens carregam corretamente.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {avatarPreviews.map((avatar, index) => (
                  <Box
                    key={index}
                    sx={{
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <img
                      src={getGoogleDriveDirectLink(avatar.url)}
                      alt={`Avatar ${index + 1}`}
                      style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover" }}
                    />
                    {avatar.isAccessible ? (
                      <Tooltip title="Imagem acessível">
                        <CheckCircleOutlineIcon color="success" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="A imagem pode estar inacessível ou o link é inválido.">
                        <WarningAmberIcon color="warning" />
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {fileLoaded && csvData.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                mt: 3,
              }}
            >
              {[
                {
                  label: "Total de linhas",
                  value: csvSummary.total,
                },
                {
                  label: "Linhas validadas",
                  value: csvSummary.linhasValidadas,
                },
                {
                  label: "Linhas com erro",
                  value: csvSummary.linhasComErro,
                },
                {
                  label: "Pendentes por campos obrigatórios",
                  value: csvSummary.obrigatoriosPendentes,
                },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    border: "1px solid #E0E0E0",
                    borderRadius: 2,
                    padding: 2,
                    minWidth: 200,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: "#5C5C5C" }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h5">{item.value}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {fileLoaded && csvData.length > 0 && (
            <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 3 }}>
              {hasMissingEntity && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Registros sem entidade vinculada
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#5C5C5C", mt: 1 }}>
                    Selecione uma entidade abaixo para aplicá-la a todos os registros importados.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
                    <FormControl sx={{ minWidth: 260 }} size="small">
                      <InputLabel id="csv-entity-select-label">Entidade</InputLabel>
                      <Select
                        labelId="csv-entity-select-label"
                        value={selectedEntityId}
                        label="Entidade"
                        onChange={handleEntitySelection}
                        disabled={isLoadingEntities || entityOptions.length === 0}
                      >
                        {entityOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={fetchEntities}
                      disabled={isLoadingEntities}
                    >
                      {isLoadingEntities ? "Carregando..." : "Atualizar lista"}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Seção de Seleção e Criação de Turma (studentsgroup_id) */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Vincular a uma Turma (Opcional)
                </Typography>
                <Typography variant="body2" sx={{ color: "#5C5C5C", mt: 1 }}>
                  Selecione uma turma existente ou digite um novo nome para criá-la e aplicá-la a todos os registros.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mt: 2 }}>
                  <Autocomplete
                    sx={{ minWidth: 320 }}
                    size="small"
                    options={studentsGroups}
                    getOptionLabel={(option) => (typeof option === "string" ? option : option.name || "")}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    value={selectedGroup}
                    onChange={(event, newValue) => handleGroupChange(newValue)}
                    inputValue={groupInputValue}
                    onInputChange={(event, newInputValue) => setGroupInputValue(newInputValue)}
                    loading={isLoadingGroups || isCreatingGroup}
                    noOptionsText={
                      groupInputValue.trim() ? (
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {`Nenhuma turma encontrada com "${groupInputValue}".`}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            disabled={isCreatingGroup}
                            onClick={() => handleCreateGroup(groupInputValue)}
                          >
                            {`Criar turma "${groupInputValue}"`}
                          </Button>
                        </Box>
                      ) : (
                        "Nenhuma turma cadastrada"
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Turma"
                        placeholder="Selecione ou digite para criar..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {isLoadingGroups || isCreatingGroup ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />

                  {groupInputValue.trim() !== "" && !isExactGroupMatch && (
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AddIcon />}
                      disabled={isCreatingGroup}
                      onClick={() => handleCreateGroup(groupInputValue)}
                    >
                      {isCreatingGroup ? "Criando..." : `Criar "${groupInputValue}"`}
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    onClick={() => fetchStudentsGroups()}
                    disabled={isLoadingGroups}
                  >
                    {isLoadingGroups ? "Carregando..." : "Atualizar turmas"}
                  </Button>
                </Box>
              </Box>

              {hasMissingPassword && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Registros sem senha definida
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#5C5C5C", mt: 1 }}>
                    Digite ou gere uma senha para aplicar automaticamente a todas as linhas do CSV.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
                    <TextField
                      label="Senha padrão"
                      size="small"
                      type="text"
                      value={passwordOverride}
                      onChange={handlePasswordInputChange}
                      sx={{ minWidth: 260 }}
                    />
                    <Button variant="contained" onClick={handleApplyPasswordClick}>
                      Aplicar senha
                    </Button>
                    <Button variant="outlined" onClick={handleGeneratePassword}>
                      Gerar senha
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {isImporting && (
            <Box sx={{ width: "100%", mt: 4 }}>
              <Typography sx={{ mb: 1 }}>
                Processando {importProgress.processed} de {importProgress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(importProgress.processed / importProgress.total) * 100}
              />
            </Box>
          )}

          {fileLoaded && csvData.length > 0 ? (
            <CsvTable headers={headers} data={csvData} fileName={fileName} />
          ) : (
            <Box sx={{ my: 4, textAlign: "center" }}>
              <Typography variant="body1">
                Nenhum arquivo carregado. Por favor, adicione um arquivo CSV.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Modal de confirmação */}
      <Dialog
        open={confirmDialogOpen}
        onClose={cancelSendToApi}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirmar importação
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Tem certeza que deseja criar o cadastro?<br />
            <Alert severity="info">Esse processo pode levar alguns minutos. Aguarde e acompanhe o progresso na tabela.</Alert>
          </DialogContentText>
        </DialogContent>

        <Divider sx={{ my: 2 }} />

        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={cancelSendToApi}
            color="secondary"
            variant="contained"
          >
            Não
          </Button>
          <Button
            onClick={confirmSendToApi}
            color="primary"
            variant="contained"
            autoFocus
            sx={{ mx: 1 }}
          >
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      {!arquivoEstaVazio() && (
        <Box
          className={styles.container}
          sx={{
            [theme.breakpoints.down("sm")]: {
              width: "100%",
            },
          }}
        >
          <Typography>
            {cadastroConcluido
              ? "Cadastro concluído! Na tabela se encontra o resultado da importação. Alguns cadastros podem ter falhado, verifique a coluna criado para mais detalhes."
              : "Após revisar as informações, confirme a criação do cadastro clicando em CRIAR CADASTRO."}
          </Typography>

          <Box sx={{ width: "100%", mt: 2 }}>
            {!verificarTodosStatusErrados() && !cadastroConcluido && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendToApi}
                disabled={isImporting}
              >
                {isImporting ? "Importando..." : "Criar cadastro"}
              </Button>
            )}
            <Button
              variant="contained"
              sx={{ ml: 2 }}
              color="secondary"
              onClick={handleBack}
              disabled={isImporting}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      )}
      <ToastContainer />
    </Box>
  );
}
